/**
 * Types, constants and utilities related to datacharts
 */
import * as React from "react";
import isEqual from "lodash/isEqual";
import isEqualWith from "lodash/isEqualWith";
import { getColor } from "theme/helpers/utils";
import { usePolledQuery } from "../hooks/apollo";
import {
  GetMeasurementStation_station_datasources,
  GetMeasurementStation_station_datasources_datasource_annotation,
} from "../utils/__generated__/GetMeasurementStation";
import {
  GetDatasourceDataByUUID,
  GetDatasourceDataByUUIDVariables,
  GetDatasourceDataByUUID_source,
} from "utils/__generated__/GetDatasourceDataByUUID";
import { alertFragment } from "../pages/__generated__/alertFragment";
import { GET_DATASOURCE_DATA_BY_UUID } from "../utils/stations";
import { GetDatasourceData_source_data } from "utils/__generated__/GetDatasourceData";
import { SourceVisualSettings } from "./widgets";

export interface DataPoint {
  timestamp: string;
  value: number;
  invalid: boolean | null;
}

export type DataSource = GetMeasurementStation_station_datasources;

export type DataSourceAnnotation =
  GetMeasurementStation_station_datasources_datasource_annotation;

export interface AdditionalDatapointData {
  source: GetDatasourceDataByUUID_source;
  data: GetDatasourceData_source_data[];
  subtractData?: GetDatasourceData_source_data[];
  seriesData?: Object;
}

export interface DataChartProps {
  source: GetMeasurementStation_station_datasources;
  datapoints: DataPoint[] | null;
  showAnnotationForm?: (source: DataSource, datapoint: DataPoint) => void;
  medianDatapoints?: DataPoint[] | null;
  alerts?: alertFragment[];
  startDate?: Date;
  endDate?: Date;
  dataModifier?: number;
  additionalDatapoints?: AdditionalDatapointData[];
  confidenceBand?: boolean;
  datapointGroupDateFormat?: string | null;
  labelDateFormat?: string | null;
  scalingMin?: number;
  scalingMax?: number;
  manualScaling?: boolean;
  sourceVisualSettings?: SourceVisualSettings[];
  isAdmin?: boolean;
  showAnnotations?: boolean;
  ignoreZeroValues?: boolean;
  showPointDataAsGraph?: boolean;
  lineColors?: string[];
}

export const defaultAnnotationColor = "rgba(187, 127, 0, 0.8)";

export const getChartSeriesColor = (seriesIndex?: number): string => {
  if (!seriesIndex) {
    return getColor("primary");
  }
  const colors: string[] = [
    "#E8743B",
    "#5899DA",
    "#EB054A",
    "#945ECF",
    "#13A4B4",
    "#525DF4",
    "#BF399E",
    "#6C8893",
    "#EE6868",
    "#2F6497",
  ];
  const idx = (seriesIndex - 1) % colors.length;
  return colors[idx];
};

export const isValidDataPointValue = (value: any): boolean => {
  // Ensure that the datapoint is not null, undefined, empty string, NaN or a "NaN" string
  // ("NaN" strings have been encountered in the datapoint table)
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    value !== "NaN" &&
    !isNaN(Number(value))
  );
};

/** UUIDs from the widget settings and from Hasura may differ in case. */
const uuidKey = (uuid: any): string => String(uuid ?? "").toLowerCase();

export const isSameUUID = (a: any, b: any): boolean =>
  !!a && !!b && uuidKey(a) === uuidKey(b);

/**
 * Whether `existing` already holds exactly this source and data. Compared by
 * content, not by size: after a date range change or a datapoint edit the new
 * data often has as many points as the old one. isEqual returns early for
 * identical references, which is the common case.
 */
export const isSameSourceData = (
  existing: AdditionalDatapointData | undefined,
  source: GetDatasourceDataByUUID_source,
  data: GetDatasourceData_source_data[],
): boolean =>
  !!existing &&
  isEqual(existing.data, data) &&
  // source.data is the same array as `data`; do not compare it twice.
  isEqualWith(existing.source, source, (_a, _b, key) =>
    key === "data" ? true : undefined,
  );

/**
 * Replaces the entry of `source` in `list`, drops entries of sources not in
 * `sourceUUIDs` and orders the list like `sourceUUIDs`.
 *
 * Returns `list` itself when nothing changes, so that a state update bails
 * out instead of re-rendering (and re-triggering the loaders).
 */
export const upsertSourceData = (
  list: AdditionalDatapointData[],
  source: GetDatasourceDataByUUID_source,
  data: GetDatasourceData_source_data[],
  sourceUUIDs: string[],
): AdditionalDatapointData[] => {
  if (!sourceUUIDs.some((uuid) => isSameUUID(uuid, source.source_uuid))) {
    return list;
  }
  const existing = list.find((d) =>
    isSameUUID(d.source.source_uuid, source.source_uuid),
  );
  if (existing && existing.source === source && existing.data === data) {
    return list;
  }
  return orderBySourceUUIDs(
    [
      ...list.filter(
        (d) => !isSameUUID(d.source.source_uuid, source.source_uuid),
      ),
      { source, data },
    ],
    sourceUUIDs,
  );
};

/** Keeps the entries of `sourceUUIDs`, in that order. */
export const orderBySourceUUIDs = (
  list: AdditionalDatapointData[],
  sourceUUIDs: string[],
): AdditionalDatapointData[] => {
  const position = new Map<string, number>();
  sourceUUIDs.forEach((uuid, idx) => {
    if (!position.has(uuidKey(uuid))) position.set(uuidKey(uuid), idx);
  });
  return list
    .filter((d) => position.has(uuidKey(d.source.source_uuid)))
    .sort(
      (a, b) =>
        position.get(uuidKey(a.source.source_uuid))! -
        position.get(uuidKey(b.source.source_uuid))!,
    );
};

const NO_SOURCE_DATA: AdditionalDatapointData[] = [];

/** Identifies a date range; null and undefined dates mean "open". */
export const getDateRangeKey = (
  startDate?: Date | null,
  endDate?: Date | null,
): string => `${startDate?.getTime() ?? ""}|${endDate?.getTime() ?? ""}`;

/**
 * State for widgets that load several sources with FullDataSourceLoader.
 * Returns the loaded data of the configured sources, in configured order, and
 * the setSourceData callback for the loaders.
 *
 * The data belongs to the date range it was loaded for. When the range
 * changes, data of the previous range is no longer returned: otherwise a
 * widget would mix ranges while loading, and keep showing the previous
 * range for a source whose new query fails. Ranges in the Apollo cache are
 * delivered in the same commit, so switching between them does not flicker.
 *
 * The update is functional: loaders that deliver in the same commit (cached
 * queries, date range changes) would otherwise overwrite each other.
 */
export const useCombinedSourceData = (
  sourceUUIDs: string[] | undefined,
  startDate?: Date | null,
  endDate?: Date | null,
): [
  AdditionalDatapointData[],
  (
    source: GetDatasourceDataByUUID_source,
    data: GetDatasourceData_source_data[],
  ) => void,
] => {
  const [loaded, setLoaded] = React.useState<{
    rangeKey: string;
    list: AdditionalDatapointData[];
  }>({ rangeKey: "", list: NO_SOURCE_DATA });
  const rangeKey = getDateRangeKey(startDate, endDate);
  // A setSourceData of a previous range must not replace the current data.
  const currentRangeKey = React.useRef(rangeKey);
  currentRangeKey.current = rangeKey;
  // Keyed by content: callers may pass a new but equal array on every render.
  const configuredKey = (sourceUUIDs ?? []).join("\n");
  const configured = React.useMemo(
    () => (configuredKey ? configuredKey.split("\n") : []),
    [configuredKey],
  );
  // Likewise, an older setSourceData must not prune with an older list.
  const currentConfigured = React.useRef(configured);
  currentConfigured.current = configured;

  const setSourceData = React.useCallback(
    (
      source: GetDatasourceDataByUUID_source,
      data: GetDatasourceData_source_data[],
    ) => {
      if (rangeKey !== currentRangeKey.current) {
        return;
      }
      setLoaded((previous) => {
        const sameRange = previous.rangeKey === rangeKey;
        const list = sameRange ? previous.list : NO_SOURCE_DATA;
        const next = upsertSourceData(
          list,
          source,
          data,
          currentConfigured.current,
        );
        return sameRange && next === list ? previous : { rangeKey, list: next };
      });
    },
    [rangeKey],
  );

  // Sources removed from the settings and data of a previous date range
  // disappear right away, not only after the next delivery.
  const currentList =
    loaded.rangeKey === rangeKey ? loaded.list : NO_SOURCE_DATA;
  const combined = React.useMemo(
    () =>
      currentList.length
        ? orderBySourceUUIDs(currentList, configured)
        : NO_SOURCE_DATA,
    [currentList, configured],
  );

  return [combined, setSourceData];
};

/**
 * Loads one source and hands it to `setSourceData` whenever
 * `additionalDatapoints` does not already hold exactly the loaded source and
 * data, e.g. after the date range changes. `setSourceData` must store the
 * given objects (or equal copies), otherwise the loader delivers again on
 * every render.
 */
export const FullDataSourceLoader = ({
  sourceUUID,
  startDate,
  endDate,
  setSourceData,
  additionalDatapoints,
}: {
  sourceUUID: string;
  startDate?: Date;
  endDate?: Date;
  additionalDatapoints?: AdditionalDatapointData[];
  setSourceData: (
    source: GetDatasourceDataByUUID_source,
    data: GetDatasourceData_source_data[],
  ) => void;
}) => {
  const {
    data: dataSourceData,
    loading,
    error,
  } = usePolledQuery<GetDatasourceDataByUUID, GetDatasourceDataByUUIDVariables>(
    GET_DATASOURCE_DATA_BY_UUID,
    {
      variables: {
        sourceUUID,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      },
    },
  );

  // When the variables change (e.g. a new date range), Apollo 3.5 returns
  // the previous variables' result once more, with loading not set. Remember
  // the result from before the change so that it is not delivered as data
  // of the new variables.
  const variablesKey = `${sourceUUID}|${startDate?.toISOString()}|${endDate?.toISOString()}`;
  const results = React.useRef<{
    variablesKey: string;
    latest: GetDatasourceDataByUUID | undefined;
    beforeChange: GetDatasourceDataByUUID | undefined;
  }>({ variablesKey, latest: undefined, beforeChange: undefined });
  if (results.current.variablesKey !== variablesKey) {
    results.current = {
      variablesKey,
      latest: dataSourceData,
      beforeChange: results.current.latest,
    };
  } else {
    results.current.latest = dataSourceData;
  }
  const isPreviousVariablesResult =
    !!dataSourceData && dataSourceData === results.current.beforeChange;

  const dataSource = dataSourceData?.source;
  const datapoints = React.useMemo(
    () => dataSourceData?.source?.data,
    [dataSourceData],
  );

  React.useEffect(() => {
    // Delivering the previous variables' data now would label it as data
    // of the new date range.
    if (loading || error || isPreviousVariablesResult) {
      return;
    }
    if (datapoints === undefined || datapoints === null || !dataSource) {
      return;
    }

    // TODO: FIX THE REAL ISSUE HERE. See trello card 410 for details.
    if (
      !dataSource.source_id &&
      !dataSource.source_uuid &&
      !dataSource.type &&
      !dataSource.station
    ) {
      return;
    }

    const existingData = additionalDatapoints?.find((d) =>
      isSameUUID(d.source.source_uuid, sourceUUID),
    );
    if (isSameSourceData(existingData, dataSource, datapoints)) {
      return;
    }

    setSourceData(dataSource, datapoints);
  }, [
    setSourceData,
    sourceUUID,
    datapoints,
    additionalDatapoints,
    dataSource,
    loading,
    error,
    isPreviousVariablesResult,
  ]);
  return <></>;
};
